import React from "react";
import {
  Box, Chip, ListItem, TextField, Typography, Button,
} from "@mui/material";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import { SimulationChangeType } from "../../interfaces/DTO/simulation";
import { formatSimulationChangeType, colorSimulationChangeType } from "../../utils/boardTemplates";
import { formatAdjustedProductType } from "../../utils/enumFormatter";
import { formatCurrency, formatHandlerCode } from "../../utils/namesFormater";
import type { ChangeListItemData } from "./ChangeListItem";

export interface InsuranceCoverageData {
  primaryBefore: number;
  primaryAfter: number;
  secondaryBefore: number;
  secondaryAfter: number;
  monthlyCostBefore: number;
  monthlyCostAfter: number;
}

export interface InsuranceChangeListItemData extends ChangeListItemData {
  insuranceCoverage?: InsuranceCoverageData;
}

interface Props {
  item: InsuranceChangeListItemData;
  noteValue: string;
  editingNoteKey: string | null;
  notesOnlyMode: boolean;
  listItemBgColor?: string;
  contactColorMap: Record<number, string>;
  showAdjustedProductType?: boolean;
  onNoteClick: (policyNumber: string, userNote: string) => void;
  onNoteChange: (policyNumber: string, value: string) => void;
  onNoteBlur: (policyNumber: string) => void;
  onNoteUpdate?: (policyNumber: string, note: string) => void;
  onNoteCancel: (policyNumber: string, userNote: string) => void;
}

const InsuranceChangeListItem: React.FC<Props> = ({
  item,
  noteValue,
  editingNoteKey,
  notesOnlyMode,
  listItemBgColor,
  contactColorMap,
  showAdjustedProductType = false,
  onNoteClick,
  onNoteChange,
  onNoteBlur,
  onNoteUpdate,
  onNoteCancel,
}) => {
  const cov = item.insuranceCoverage;
  const isHandlerChanged = item.originalHandlerCode !== null && item.originalHandlerCode !== item.handlerCode;
  const handlerDisplay = isHandlerChanged
    ? `${formatHandlerCode(item.originalHandlerCode)} ← ${formatHandlerCode(item.handlerCode)}`
    : formatHandlerCode(item.handlerCode);

  return (
    <ListItem
      sx={{
        borderRadius: 1,
        mb: 0.5,
        backgroundColor: listItemBgColor ?? "#fff",
        border: "1px solid #e0e0e0",
        ...(item.contactId && contactColorMap[item.contactId] ? { borderRight: `6px solid ${contactColorMap[item.contactId]}` } : {}),
        display: "grid",
        gridTemplateColumns: "5fr 5fr 2fr",
        alignItems: "start",
        gap: 1,
        py: 1,
        px: 2,
      }}
    >
      {/* Col 1: details */}
      <Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5, flexWrap: "wrap" }}>
          <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
            {item.changeTypes.map((ct, i) => (
              <Chip
                key={i}
                label={formatSimulationChangeType(ct)}
                size="small"
                sx={{ backgroundColor: colorSimulationChangeType(ct), color: "#fff", fontWeight: 600, fontSize: "0.8rem", height: 24 }}
              />
            ))}
          </Box>
          {showAdjustedProductType && item.adjustedProductType != null && (
            <Typography variant="body2" color="text.secondary">
              {formatAdjustedProductType(item.adjustedProductType)}
            </Typography>
          )}
          {!item.changeTypes.includes(SimulationChangeType.OPEN_NEW) && (
            <Typography variant="body2" color="text.secondary" sx={{ minWidth: 100 }}>
              {item.policyNumber}
            </Typography>
          )}
        </Box>

        <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
          {handlerDisplay && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Typography variant="body2" color="text.secondary">יצרן:</Typography>
              <Typography variant="body2" sx={{ fontWeight: isHandlerChanged ? 600 : 400 }}>{handlerDisplay}</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Col 2: notes */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        <Box sx={{ cursor: "pointer" }} onClick={() => onNoteClick(item.policyNumber, item.userNote)}>
          {editingNoteKey === item.policyNumber ? (
            <Box>
              <TextField
                autoFocus
                multiline
                rows={3}
                size="small"
                fullWidth
                value={noteValue}
                onChange={(e) => onNoteChange(item.policyNumber, e.target.value)}
                onBlur={() => onNoteBlur(item.policyNumber)}
                placeholder="הערה..."
                inputProps={{ dir: "rtl", style: { direction: "rtl", textAlign: "right", unicodeBidi: "plaintext" } }}
                onFocus={(e) => { const len = e.target.value.length; e.target.setSelectionRange(len, len); }}
                onClick={(e) => e.stopPropagation()}
              />
              {notesOnlyMode && (
                <Box sx={{ display: "flex", gap: 1, mt: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  <Button size="small" variant="contained" onClick={() => onNoteUpdate?.(item.policyNumber, noteValue)}>עדכן</Button>
                  <Button size="small" variant="outlined" onClick={() => onNoteCancel(item.policyNumber, item.userNote)}>בטל</Button>
                </Box>
              )}
            </Box>
          ) : noteValue || item.autoHighlight ? (
            <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
              <NoteAltOutlinedIcon fontSize="small" sx={{ color: "primary.main", mt: 0.2, flexShrink: 0 }} />
              <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: "pre-wrap" }}>
                {[noteValue, item.autoHighlight].filter(Boolean).join(" | ")}
              </Typography>
            </Box>
          ) : (
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <NoteAltOutlinedIcon fontSize="small" sx={{ color: "action.disabled" }} />
              <Typography variant="caption" color="text.disabled">הוסף הערה</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Col 3: insurance coverage */}
      {cov && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 0.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <Typography variant="caption" color="text.secondary">כיסוי ראשי</Typography>
            {cov.primaryAfter !== cov.primaryBefore && (
              <Typography variant="caption" color="text.disabled" sx={{ textDecoration: "line-through" }}>
                {formatCurrency(cov.primaryBefore)}
              </Typography>
            )}
            <Typography variant="body2" fontWeight={700} sx={{ color: cov.primaryAfter > cov.primaryBefore ? "success.main" : cov.primaryAfter < cov.primaryBefore ? "error.main" : "text.primary" }}>
              {formatCurrency(cov.primaryAfter)}
            </Typography>
          </Box>

          {(cov.secondaryBefore > 0 || cov.secondaryAfter > 0) && (
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
              <Typography variant="caption" color="text.secondary">כיסוי משני</Typography>
              {cov.secondaryAfter !== cov.secondaryBefore && (
                <Typography variant="caption" color="text.disabled" sx={{ textDecoration: "line-through" }}>
                  {formatCurrency(cov.secondaryBefore)}
                </Typography>
              )}
              <Typography variant="body2" fontWeight={700} sx={{ color: cov.secondaryAfter > cov.secondaryBefore ? "success.main" : cov.secondaryAfter < cov.secondaryBefore ? "error.main" : "text.primary" }}>
                {formatCurrency(cov.secondaryAfter)}
              </Typography>
            </Box>
          )}

          <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
            <Typography variant="caption" color="text.secondary">עלות חודשית</Typography>
            {cov.monthlyCostAfter !== cov.monthlyCostBefore && (
              <Typography variant="caption" color="text.disabled" sx={{ textDecoration: "line-through" }}>
                {formatCurrency(cov.monthlyCostBefore)}
              </Typography>
            )}
            <Typography variant="body2" fontWeight={700} sx={{ color: cov.monthlyCostAfter > cov.monthlyCostBefore ? "error.main" : cov.monthlyCostAfter < cov.monthlyCostBefore ? "success.main" : "text.primary" }}>
              {formatCurrency(cov.monthlyCostAfter)}
            </Typography>
          </Box>
        </Box>
      )}
    </ListItem>
  );
};

export default InsuranceChangeListItem;
